import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/**
 * Fades + lifts its children into view once, when scrolled to.
 * Renders as a plain wrapper when the visitor prefers reduced motion.
 */
const Reveal = ({ children, delay = 0, y = 24, style }) => {
  const reduceMotion = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return undefined;
    if (inView) {
      setRevealed(true);
      return undefined;
    }

    // IntersectionObserver samples on frame boundaries, so a fast jump (End
    // key, restored scroll position on reload, a trackpad flick) can carry an
    // element past the viewport between two samples. `once` then never
    // re-checks and the section stays at opacity 0 forever. Scroll events fire
    // continuously, so this catches anything the observer skipped.
    const check = () => {
      const el = ref.current;
      if (el && el.getBoundingClientRect().top < window.innerHeight) {
        setRevealed(true);
      }
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, [inView, revealed]);

  if (reduceMotion) {
    return (
      <div ref={ref} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      style={style}
      initial={{ opacity: 0, y }}
      animate={revealed ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default Reveal;
